import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';
import { ToastContainer as CustomToastContainer } from './components/common/CustomToast';
import { DepositNotificationContainer } from './components/common/DepositNotification';
import { ChatSidebar } from './components/chat';
import ChatToggleButton from './components/chat/ChatToggleButton';
import { ReferralHandler } from './components/ReferralHandler';
import LoadingScreen from './components/common/LoadingScreen';
import { store, RootState } from './store/store';
import { updateBalance, updateUser } from './store/slices/authSlice';
import { Header } from './components/header';
import { CoinflipPage, SettingsPage, LoginPage, RegisterPage, AdminPage, NotFoundPage, TermsOfServicePage, UnderReviewPage } from './pages';
import ProvablyFairPage from './pages/ProvablyFairPage';
import WaitingPage from './pages/WaitingPage';
import GameModeDisabled from './components/GameModeDisabled';
import BannedPage from './components/BannedPage';
import { useActivityTracker } from './hooks';
import { socketService } from './services/SocketService';
import { userService } from './services/UserService';

import './App.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AppContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isConnected, user, token } = useSelector((state: RootState) => state.auth);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [gameModeStatus, setGameModeStatus] = useState({ coinflip: false }); // coinflip enabled by default
  const [userBanStatus, setUserBanStatus] = useState<{ isBanned: boolean; banReason?: string; banExpiresAt?: string; isUnderReview?: boolean } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Chat is open by default for logged out users, closed by default for logged in users
  const [isChatVisible, setIsChatVisible] = useState(!isConnected);
  
  // Cache to prevent duplicate API calls - using ref to avoid unnecessary re-renders
  const lastApiCallsRef = useRef<{[key: string]: number}>({});
  
  // Track user activity to keep session alive
  useActivityTracker();

  // Initial loading effect
  useEffect(() => {
    // Minimum loading time of 2 seconds
    const minLoadTime = 2000;
    const startTime = Date.now();

    const finishLoading = () => {
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadTime - elapsed);
      
      setTimeout(() => {
        setIsLoading(false);
      }, remainingTime);
    };

    // Wait for document to be fully loaded
    if (document.readyState === 'complete') {
      finishLoading();
    } else {
      window.addEventListener('load', finishLoading);
      return () => window.removeEventListener('load', finishLoading);
    }
  }, []);

  // Update chat visibility when login state changes
  useEffect(() => {
    // Open chat by default when user logs out
    if (!isConnected) {
      setIsChatVisible(true);
    }
  }, [isConnected]);

  // Setup socket connection and user data updates
  useEffect(() => {
    if (isConnected && user && token) {
      // Connect to WebSocket
      socketService.connect(user.id);
      
      // Subscribe to balance updates
      const unsubscribe = socketService.subscribeToBalanceUpdates((newBalance) => {
        dispatch(updateBalance(newBalance));
      });
      
      // Refresh user data on app load to get current balance, XP, level, etc. from database
      const refreshUserData = async () => {
        try {
          const userData = await userService.getCurrentUser();
          if (userData) {
            // Update all user data (balance, stats, etc.) since localStorage doesn't store dynamic data
            dispatch(updateUser({
              balance: userData.balance,
              totalWagered: userData.totalWagered,
              totalWon: userData.totalWon,
              totalLost: userData.totalLost
            }));
          }
        } catch (error) {
          // Silently handle errors - user will see login prompt if needed
        }
      };
      
      // Load user data immediately when user is loaded
      refreshUserData();
      
      // Periodic refresh every 5 minutes (reduced to prevent rate limiting)
      const userDataInterval = setInterval(refreshUserData, 300000);
      
      return () => {
        unsubscribe();
        clearInterval(userDataInterval);
      };
    } else {
      // Disconnect when user logs out
      socketService.disconnect();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, user?.id, token]); // Only re-run when connection status, user ID, or token changes

  // Check maintenance mode and gamemode status (runs for all users, logged in or not)
  useEffect(() => {
    const checkMaintenanceMode = async () => {
      const now = Date.now();
      const lastCall = lastApiCallsRef.current['maintenance'] || 0;
      if (now - lastCall < 60000) return; // Skip if called within last minute
      
      try {
        lastApiCallsRef.current['maintenance'] = now;
        const response = await fetch(`${API_BASE_URL}/api/maintenance`);
        if (response.ok) {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const data = await response.json();
            setMaintenanceMode(data.enabled);
          }
        }
      } catch (error) {
        console.error('Error checking maintenance mode:', error);
      }
    };

    const checkGameModeStatus = async () => {
      const now = Date.now();
      const lastCall = lastApiCallsRef.current['gamemode'] || 0;
      if (now - lastCall < 120000) return; // Skip if called within last 2 minutes
      
      try {
        lastApiCallsRef.current['gamemode'] = now;
        const response = await fetch(`${API_BASE_URL}/api/users/gamemode-status`);
        if (response.ok) {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const data = await response.json();
            setGameModeStatus(data);
          }
        }
      } catch (error) {
        console.error('Error checking gamemode status:', error);
      }
    };

    // Check immediately on mount
    checkMaintenanceMode();
    checkGameModeStatus();
    
    // Then check periodically
    const maintenanceInterval = setInterval(checkMaintenanceMode, 300000); // Every 5 minutes
    const gamemodeInterval = setInterval(checkGameModeStatus, 300000); // Every 5 minutes
    
    return () => {
      clearInterval(maintenanceInterval);
      clearInterval(gamemodeInterval);
    };
  }, []); // No dependencies - runs once on mount and sets up intervals

  // Check user ban status
  useEffect(() => {
    const checkBanStatus = async () => {
      if (user && token) {
        try {
          const response = await fetch(`${API_BASE_URL}/api/users/ban-status`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.status === 403) {
            // User is banned, parse the ban info from the 403 response
            const data = await response.json();
            if (data.reason && data.expiresAt) {
              setUserBanStatus({
                isBanned: true,
                banReason: data.reason,
                banExpiresAt: data.expiresAt
              });
            }
            return;
          }
          
          if (response.ok) {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const data = await response.json();
              setUserBanStatus(data);
            }
          }
        } catch (error) {
          // Silently handle - user will see appropriate page
        }
      }
    };

    checkBanStatus();
  }, [user, token]);

  // Only redirect to login if not logged in and on protected pages
  useEffect(() => {
    const protectedPages = ['/settings', '/transactions', '/moneymandavetj'];
    const currentPath = location.pathname;
    
    if (!isConnected && protectedPages.includes(currentPath)) {
      navigate('/login');
    }
  }, [isConnected, location.pathname, navigate]);

  // Handle /waiting route specifically
  if (location.pathname === '/waiting') {
    if (!maintenanceMode) {
      return <NotFoundPage />;
    }
    return <WaitingPage />;
  }

  // Redirect to waiting page if maintenance mode is enabled (except for admins)
  if (maintenanceMode && (!user?.isAdmin || location.pathname !== '/moneymandavetj')) {
    return <WaitingPage />;
  }

  // Show banned page if user is banned
  if (userBanStatus?.isBanned && userBanStatus.banReason && userBanStatus.banExpiresAt) {
    return <BannedPage banReason={userBanStatus.banReason} banExpiresAt={userBanStatus.banExpiresAt} />;
  }

  // Show under review page if user is under review
  if (userBanStatus?.isUnderReview) {
    return <UnderReviewPage />;
  }

  // Show loading screen
  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className={`App ${isChatVisible ? 'chat-open' : ''}`}>
      {/* Referral Handler - processes ?ref= parameter for logged-in users */}
      <ReferralHandler />
      
      {/* Main app content that will shrink when chat is open */}
      <div className="main-app-content">
        {/* Show header on all pages except login/register */}
        {location.pathname !== '/login' && location.pathname !== '/register' && (
          <Header />
        )}
        
        {/* Main Content Area */}
        <main className="main-content">
          <div className="content-container">
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/" element={
                gameModeStatus.coinflip ? <GameModeDisabled gameMode="Coinflip" /> : <CoinflipPage />
              } />
              <Route path="/coinflip" element={
                gameModeStatus.coinflip ? <GameModeDisabled gameMode="Coinflip" /> : <CoinflipPage />
              } />
              <Route path="/provably-fair" element={<ProvablyFairPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/banned" element={userBanStatus?.isBanned && userBanStatus.banReason && userBanStatus.banExpiresAt ? <BannedPage banReason={userBanStatus.banReason} banExpiresAt={userBanStatus.banExpiresAt} /> : <NotFoundPage />} />
              <Route path="/under-review" element={<UnderReviewPage />} />
              <Route path="/admin" element={<NotFoundPage />} />
              <Route path="/moneymandavetj" element={<AdminPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </div>
        </main>
        <CustomToastContainer />
        <DepositNotificationContainer />
      </div>
      
      {/* Chat System - Now part of the flex layout */}
      <ChatToggleButton 
        isOpen={isChatVisible} 
        onToggle={() => setIsChatVisible(!isChatVisible)} 
      />
      <ChatSidebar 
        isOpen={isChatVisible} 
        onToggle={() => setIsChatVisible(!isChatVisible)} 
      />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <HelmetProvider>
        <Router>
          <AppContent />
        </Router>
      </HelmetProvider>
    </Provider>
  );
};

export default App;
